import {DrawFrmApp} from "./DrawFrmApp";
import {DrawFrmThing, Disposable} from "./DrawFrmThing";
import {ShowReactInput} from "./DrawFrmDisplay";
import {FrmSceneLayout} from "./FrmSceneLayout";
import {Subscription} from "nate-react-api-helpers";

export interface Scene {
    setup(draw: DrawFrmApp): void;
    dispose(): void;
    applyLayout(): void;
}

export class ManagedScene implements Scene {
    protected app: DrawFrmApp | null = null;
    protected items: DrawFrmThing[] = [];

    // arbitrary resources tied to this scene's lifecycle, torn down in dispose()
    // (e.g. subscriptions, timers). Registered via track().
    private tracked: Disposable[] = [];

    // the active layout, kept around so it can be re-applied when a child's
    // bounding box changes. see setupLayout().
    private layoutRoot: FrmSceneLayout | null = null;
    private layoutSubs: Subscription[] = [];
    private relayoutScheduled = false;
    private relayoutHandle: number | null = null;

    setup(app: DrawFrmApp) {
        this.app = app;
    }

    add(item: DrawFrmThing) {
        this.items.push(item);
        this.app?.add(item);
    }

    // register a resource to be cleaned up when this scene is destroyed.
    track<T extends Disposable>(input: T) {
        this.tracked.push(input);
        return input;
    }

    // arrange the scene's graphics with a flex-box style layout. The root fills
    // the current viewport (scene space == screen pixels at the identity
    // transform), and the user's pan/zoom still applies on top.
    setupLayout(root: FrmSceneLayout) {
        if(!this.app) {
            throw new Error("No app, super.setup() not called");
        }

        // drop any listeners from a previous setupLayout call before re-wiring.
        this.clearLayoutSubs();
        this.layoutRoot = root;

        this.applyLayout();

        // re-flow whenever a child's geometry changes its bounding box (e.g. a
        // dimension edit reshapes the profile, or a linked item shifts). The
        // group transforms applied below are multiplicative relative to current
        // bounds, so re-applying is self-correcting and converges.
        for(const item of root.collectItems()) {
            this.layoutSubs.push(item.changed.subscribe(() => this.scheduleRelayout()));
        }
    }

    applyLayout() {
        if(!this.app || !this.layoutRoot || this.app.layoutFrozen) {
            return;
        }

        const screen = this.app.pixi.screen;
        const width = screen.width > 0 ? screen.width : 800;
        const height = screen.height > 0 ? screen.height : 600;

        this.layoutRoot.apply({x: 0, y: 0, width, height});
    }

    // batch re-layouts: many child changes can fire from one user action, so
    // coalesce them into a single re-flow on the next frame.
    private scheduleRelayout() {
        if(this.relayoutScheduled) {
            return;
        }

        this.relayoutScheduled = true;
        this.relayoutHandle = requestAnimationFrame(() => {
            this.relayoutScheduled = false;
            this.relayoutHandle = null;
            this.applyLayout();
        });
    }

    private clearLayoutSubs() {
        for(const sub of this.layoutSubs) {
            sub.cancel();
        }
        this.layoutSubs = [];

        if(this.relayoutHandle !== null) {
            cancelAnimationFrame(this.relayoutHandle);
            this.relayoutHandle = null;
        }
        this.relayoutScheduled = false;
    }

    // tear down the scene's built content (items, tracked resources, layout) but
    // keep the app + view transform, so a scene can rebuild itself in place (e.g.
    // when the model structure changes). Anything the subclass wants to survive a
    // rebuild (a model subscription driving the rebuild) must be held outside
    // items/tracked.
    protected resetContent() {
        this.clearLayoutSubs();
        this.layoutRoot = null;

        for (const item of this.items) {
            this.app?.remove(item);
            item.destroy();
        }

        this.items = [];

        for (const item of this.tracked) {
            item.destroy();
        }

        this.tracked = [];
    }

    dispose() {
        this.resetContent();
        this.app = null;
    }

    protected showReact(input: ShowReactInput) {
        if(!this.app) {
            throw new Error("No app, super.setup() not called");
        }

        this.app?.showReact(input);
    }
}